import { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Container,
  Card,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { Skeleton } from "@material-ui/lab";
// @material-ui/icons components
import {
  Create,
  Delete,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from "@material-ui/icons";

// core components
import HeaderClientes from "components/_refactor/headers/clients";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable.js";
import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import MaskAndValid from "utils/maskAndValid";
import FormIncidentDialog from "components/Dialogs/FormIncidentDialog";
import { Search } from "react-bootstrap-table2-toolkit";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import useLogs from "hooks/logs";
import { IsMobile } from "utils/is-mobile";

const getTableColumns = (setSelectedIncident, toggleFormDialog, searching) => [
  {
    dataField: "IdIncidente",
    text: "Nº do incidente",
    sort: true,
    align: "center",
    formatter: (value) => (searching ? <Skeleton variant="text" /> : value),
    headerStyle: { maxWidth: 95, minWidth: 95, width: 95 },
  },
  {
    dataField: "Status",
    text: "Status",
    sort: true,
    formatter: (value) => (searching ? <Skeleton variant="text" /> : value),
  },
  {
    dataField: "Sistema",
    text: "Sistema",
    sort: true,
    formatter: (value) => (searching ? <Skeleton variant="text" /> : value),
  },
  {
    dataField: "DescricaoOcorrido",
    text: "Descrição do ocorrido",
    sort: true,
    formatter: (value) => (searching ? <Skeleton variant="text" /> : value),
  },
  {
    dataField: "DataAbertura",
    text: "Data de Abertura",
    formatter: (value) =>
      searching ? (
        <Skeleton variant="text" />
      ) : (
        MaskAndValid.formattDateBR(value)
      ),
    sort: true,
  },
  {
    dataField: "DataResolucao",
    text: "Data de Resolução",
    formatter: (value) =>
      searching ? (
        <Skeleton variant="text" />
      ) : value !== "0001-01-01T00:00:00" ? (
        MaskAndValid.formattDateBR(value)
      ) : null,
    sort: true,
  },
  {
    dataField: "NomeUsuario",
    text: "Usuário",
    sort: true,
    formatter: (value) => (searching ? <Skeleton variant="text" /> : value),
  },
  {
    dataField: "editar",
    text: "",
    isDummyField: true,
    headerStyle: { width: 45 },
    formatter: (cell, row) => {
      return searching ? null : (
        <Tooltip title="Editar Incidente">
          <IconButton
            aria-label="edit"
            onClick={() => {
              setSelectedIncident(row);
              toggleFormDialog("edit");
            }}
          >
            <Box
              component={Create}
              width="1.25rem!important"
              height="1.25rem!important"
            />
          </IconButton>
        </Tooltip>
      );
    },
  },
  {
    dataField: "deletar",
    text: "",
    isDummyField: true,
    headerStyle: { width: 45 },
    formatter: (cell, row) =>
      searching ? null : (
        <Tooltip title="Deletar Incidente">
          <IconButton
            aria-label="delete"
            onClick={() => {
              setSelectedIncident(row);
              toggleFormDialog("delete");
            }}
          >
            <Box
              component={Delete}
              width="1.25rem!important"
              height="1.25rem!important"
            />
          </IconButton>
        </Tooltip>
      ),
  },
];

const loadingItems = [
  { IdIncidente: "0" },
  { IdIncidente: "1" },
  { IdIncidente: "2" },
  { IdIncidente: "3" },
  { IdIncidente: "4" },
  { IdIncidente: "5" },
  { IdIncidente: "6" },
  { IdIncidente: "7" },
  { IdIncidente: "8" },
  { IdIncidente: "9" },
];

const { SearchBar } = Search;

const useStyles = makeStyles(componentStyles);

function Incidentes() {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { setLocalStorageActions, setLocalStorageApiProblem } = useLogs();

  const [incidentes, setIncidentes] = useState(loadingItems);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [searching, setSearching] = useState(true);

  const isMobile = IsMobile();

  const toggleFormDialog = useCallback((formType) => {
    if (formType) {
      setOpenFormDialog(formType);
    }
  }, []);

  const updateList = useCallback(
    async (openFormDialog, updatedMenu) => {
      if (openFormDialog === "delete") {
        const filteredIncidente = incidentes?.filter(
          (incidente) => incidente.IdIncidente !== updatedMenu.IdIncidente
        );

        setIncidentes(filteredIncidente);
      } else {
        const newMenuList = incidentes?.map((incidente) => {
          if (incidente.IdIncidente === updatedMenu.IdIncidente)
            return { ...incidente, ...updatedMenu };
          else return incidente;
        });

        setIncidentes(newMenuList);
      }
    },
    [incidentes]
  );

  const fetchList = async () => {
    await Api.get("list-incident", "adminportal", "", true)
      .then(({ Success, Result }) => {
        if (Success) {
          setIncidentes(Result);
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "98",
          screen: "Incidentes",
          problem: `Erro na chamada do endpoint list-incident - ${error}`,
        });
      })
      .finally(() => setSearching(false));
  };

  const handleRefresh = async () => {
    setSearching(true);

    return fetchList();
  };

  useEffect(() => {
    fetchList();
    setLocalStorageActions({
      nomeTela: "Incidentes",
      idMenu: "98",
      acao: "Acessou o menu Incidentes",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <HeaderClientes section="Incidentes" subsection="Administração" />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <ToolkitTable
                keyField="IdIncidente"
                dataTable={searching ? loadingItems : incidentes}
                columnModel={getTableColumns(
                  setSelectedIncident,
                  toggleFormDialog,
                  searching
                )}
                search
                allowEmpty
                CustomHeader={({ searchProps }) => (
                  <Grid
                    container
                    className="pl-4"
                    style={{ padding: isMobile ? "0 24px 10px" : "0 24px" }}
                  >
                    <Grid item xs={isMobile ? 12 : 4}>
                      <Box
                        component={Typography}
                        variant="h2"
                        marginBottom="0!important"
                      >
                        <Box component="span">Lista de Incidentes</Box>
                        <IconButtonLoader
                          size="medium"
                          extraStyle={{
                            width: 24,
                            height: 24,
                            marginLeft: "0.3rem",
                          }}
                          title="Recarregar lista"
                          onClick={handleRefresh}
                          icon={<RefreshIcon color="primary" />}
                          aria-label="refresh"
                          color="primary"
                          disabled={searching}
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={isMobile ? 12 : 8}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: isMobile ? "flex-start" : "flex-end",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <SearchBar
                        className="form-control"
                        placeholder="Pesquisar Incidente"
                        {...searchProps}
                        disabled={searching}
                      />
                      <Box
                        justifyContent="flex-end"
                        display="flex"
                        flexWrap="wrap"
                        style={{ marginLeft: isMobile ? 0 : 15 }}
                      >
                        <Button
                          variant="contained"
                          classes={{
                            root: classes.buttonRootWhite,
                          }}
                          onClick={() => toggleFormDialog("create")}
                          disabled={searching}
                        >
                          <Box
                            component={AddIcon}
                            position="relative"
                            top="-2px"
                          />{" "}
                          Criar
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                pagination={{
                  options: [25, 50, 100],
                  defaultOption: 25,
                }}
                noResultTitle="Não há incidentes registrados"
                noResultSubtitle="Registre um incidente clicando em Criar"
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
      {Boolean(openFormDialog) && (
        <FormIncidentDialog
          open
          formType={openFormDialog}
          onClose={(incidente) => {
            setSelectedIncident(null);
            if (incidente?.IdIncidente > 0) {
              if (openFormDialog === "create") {
                const newMenuList = [...incidentes, { ...incidente }];

                setIncidentes(newMenuList);
              } else {
                updateList(openFormDialog, incidente);
              }
            }
            setOpenFormDialog(null);
          }}
          selectedIncident={selectedIncident}
        />
      )}
    </>
  );
}

Incidentes.propTypes = {};

export default Incidentes;
